/**
 * Clip Bank Slide
 *
 * Slide 7: Shows the gathered video clips organized by section role.
 * Displays coverage metrics and clip previews.
 */

import { motion } from 'framer-motion'
import { useState } from 'react'
import { Film, Play, CheckCircle, AlertCircle } from 'lucide-react'
import { cn } from '@trafiflow/shared-ui'
import type { OnboardingArtifacts } from '../../../contexts/OnboardingContext'

interface ClipBankSlideProps {
  artifacts: OnboardingArtifacts
}

interface Clip {
  id: string
  role: string
  source: string
  video_url: string
  usage_intent?: string
  duration_sec?: number
}

const roleColors: Record<string, string> = {
  HOOK: 'bg-blue-500/10 border-blue-500/20 text-blue-500',
  PROBLEM: 'bg-red-500/10 border-red-500/20 text-red-500',
  SOLUTION: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-500',
  PROOF: 'bg-purple-500/10 border-purple-500/20 text-purple-500',
  CTA: 'bg-amber-500/10 border-amber-500/20 text-amber-500',
}

const roleDescriptions: Record<string, string> = {
  HOOK: 'Attention grabber',
  PROBLEM: 'Pain point',
  SOLUTION: 'Product demo',
  PROOF: 'Social proof',
  CTA: 'Call to action',
}

const sourceLabels: Record<string, string> = {
  company: 'Your Library',
  brand_ad: 'Brand Ad',
  competitor: 'Competitor',
  ai: 'AI Generated',
}

export default function ClipBankSlide({ artifacts }: ClipBankSlideProps) {
  const [playingClip, setPlayingClip] = useState<string | null>(null)

  // Extract clip bank data from artifacts
  const clipBank = artifacts.clipBank as {
    total_clips?: number
    coverage?: Record<string, number>
    clips_by_role?: Record<string, Clip[]>
    clips?: Clip[]
  } | null

  // Extract video ads from adsCollection as fallback
  const adsCollection = artifacts.adsCollection as {
    ads?: Array<{
      media_urls?: string[]
      media_type?: string
      thumbnail_url?: string
    }>
  } | null

  // Create fallback clips from video ads
  const fallbackClips: Clip[] = (adsCollection?.ads || [])
    .filter(ad => ad.media_type === 'video' && ad.media_urls?.[0])
    .map((ad, index) => ({
      id: `fallback-${index}`,
      role: 'UNASSIGNED',
      source: 'brand_ad',
      video_url: ad.media_urls![0],
    }))
    .slice(0, 10)

  const hasClipBankData = (clipBank?.clips?.length || 0) > 0 || (clipBank?.total_clips || 0) > 0
  const clips = hasClipBankData ? (clipBank?.clips || []) : fallbackClips
  const totalClips = hasClipBankData
    ? (clipBank?.total_clips || clipBank?.clips?.length || 0)
    : fallbackClips.length
  const coverage = clipBank?.coverage || {}

  // Group clips by role
  const clipsByRole = clipBank?.clips_by_role || clips.reduce((acc, clip) => {
    const role = clip.role || 'UNASSIGNED'
    if (!acc[role]) acc[role] = []
    acc[role].push(clip)
    return acc
  }, {} as Record<string, Clip[]>)

  const coveredSections = Object.values(coverage).filter(v => v > 0).length
  const totalSections = Object.keys(coverage).length || 5
  const coveragePercentage = totalSections > 0 ? Math.round((coveredSections / totalSections) * 100) : 0

  // Debug logging
  console.log('[ClipBankSlide] clipBank:', clipBank)
  console.log('[ClipBankSlide] clips:', clips)
  console.log('[ClipBankSlide] fallbackClips:', fallbackClips)

  return (
    <div className="flex flex-col items-center text-center max-w-xl mx-auto">
      {/* Icon */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2, type: 'spring' }}
        className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-pink-600 flex items-center justify-center mb-6 shadow-lg"
      >
        <Film className="w-8 h-8 text-white" />
      </motion.div>

      {/* Title */}
      <motion.h2
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-2xl font-bold text-tf-text mb-2"
      >
        Clip Bank Ready
      </motion.h2>

      <motion.p
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="text-tf-text-muted text-sm mb-5"
      >
        Video clips gathered for your ad
      </motion.p>

      {/* Stats */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="flex items-center justify-center gap-6 mb-6"
      >
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-orange-500/10 flex items-center justify-center">
            <Film className="w-4 h-4 text-orange-500" />
          </div>
          <div className="text-left">
            <p className="text-xl font-bold text-tf-text">{totalClips}</p>
            <p className="text-xs text-tf-text-muted">Total Clips</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className={cn(
            'w-9 h-9 rounded-lg flex items-center justify-center',
            coveragePercentage === 100 ? 'bg-emerald-500/10' : 'bg-amber-500/10'
          )}>
            {coveragePercentage === 100 ? (
              <CheckCircle className="w-4 h-4 text-emerald-500" />
            ) : (
              <AlertCircle className="w-4 h-4 text-amber-500" />
            )}
          </div>
          <div className="text-left">
            <p className={cn(
              'text-xl font-bold',
              coveragePercentage === 100 ? 'text-emerald-500' : 'text-amber-500'
            )}>
              {coveragePercentage}%
            </p>
            <p className="text-xs text-tf-text-muted">Coverage</p>
          </div>
        </div>
      </motion.div>

      {/* Coverage by section */}
      {Object.keys(coverage).length > 0 && (
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="w-full mb-6"
        >
          <h3 className="text-xs font-medium text-tf-text-muted text-left mb-2">
            Coverage by Section
          </h3>
          <div className="grid grid-cols-5 gap-2">
            {Object.entries(coverage).map(([section, count], i) => (
              <motion.div
                key={section}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.7 + i * 0.05 }}
                className={cn(
                  'p-2 rounded-lg text-center border',
                  count > 0
                    ? roleColors[section] || 'bg-emerald-500/10 border-emerald-500/20'
                    : 'bg-red-500/10 border-red-500/20 text-red-500',
                )}
              >
                <p className="text-[10px] font-medium truncate">{section}</p>
                <p className="text-lg font-bold">{count}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Clip previews by role */}
      {Object.keys(clipsByRole).length > 0 && (
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.9 }}
          className="w-full space-y-3"
        >
          {Object.entries(clipsByRole).slice(0, 3).map(([role, roleClips], roleIndex) => (
            <div key={role} className="text-left">
              <div className="flex items-center gap-2 mb-2">
                <span className={cn(
                  'px-2 py-0.5 rounded text-xs font-medium border',
                  roleColors[role] || 'bg-tf-bg-muted text-tf-text-muted'
                )}>
                  {role}
                </span>
                <span className="text-xs text-tf-text-muted">
                  {roleDescriptions[role] || ''} - {roleClips.length} clip{roleClips.length !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="flex gap-2">
                {roleClips.slice(0, 3).map((clip, i) => (
                  <motion.div
                    key={clip.id || i}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 1.0 + roleIndex * 0.1 + i * 0.05 }}
                    className="relative w-16 h-16 rounded-lg overflow-hidden bg-tf-bg-muted cursor-pointer group"
                    onClick={() => setPlayingClip(playingClip === clip.id ? null : clip.id)}
                  >
                    {clip.video_url ? (
                      <>
                        <video
                          src={clip.video_url}
                          className="w-full h-full object-cover"
                          muted
                          loop
                          playsInline
                          autoPlay={playingClip === clip.id}
                        />
                        <div className={cn(
                          'absolute inset-0 flex items-center justify-center bg-black/30 transition-opacity',
                          playingClip === clip.id ? 'opacity-0' : 'opacity-100 group-hover:opacity-80'
                        )}>
                          <div className="w-6 h-6 rounded-full bg-white/90 flex items-center justify-center">
                            <Play className="w-3 h-3 text-gray-800 ml-0.5" fill="currentColor" />
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Film className="w-6 h-6 text-tf-text-muted" />
                      </div>
                    )}
                    {/* Source badge */}
                    <div className="absolute bottom-0.5 right-0.5 px-1 py-0.5 rounded text-[8px] bg-black/60 text-white">
                      {sourceLabels[clip.source] || clip.source}
                    </div>
                  </motion.div>
                ))}
                {roleClips.length > 3 && (
                  <div className="w-16 h-16 rounded-lg bg-tf-bg-muted flex items-center justify-center">
                    <span className="text-xs text-tf-text-muted">+{roleClips.length - 3}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </motion.div>
      )}

      {/* Using fallback clips from ads */}
      {!hasClipBankData && fallbackClips.length > 0 && (
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="w-full pt-4 text-center"
        >
          <p className="text-xs text-tf-text-muted">
            Using video clips from your ad library
          </p>
        </motion.div>
      )}

      {/* No clips at all */}
      {totalClips === 0 && fallbackClips.length === 0 && (
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="w-full py-8 text-center"
        >
          <p className="text-sm text-tf-text-muted">
            No video clips available yet
          </p>
          <p className="text-xs text-tf-text-muted mt-2">
            Clips will be gathered in future generations
          </p>
        </motion.div>
      )}
    </div>
  )
}
